import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { sponsorsService } from '../../services/sponsors';
import { useGymAdmin } from '../context/gym-admin-context';
import SortableRows from '../components/SortableRows';
import SponsorFormDialog from '../components/SponsorFormDialog';
import type { Sponsor } from '../../types/sponsor';

/**
 * A circuit's or a competition's sponsors (issue #374): the logos its page
 * shows in a strip under the cover, in the order dragged here.
 *
 * On a stage the circuit's sponsors come first on the page, so they are shown
 * above the stage's own — read-only, since they are the circuit's to change.
 */
export default function SponsorsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { selectedGymSlug: slug, place } = useGymAdmin();

  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [inherited, setInherited] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  /** The dialog: a new sponsor (null), one being edited, or closed (undefined). */
  const [editing, setEditing] = useState<Sponsor | null | undefined>(undefined);
  const [removing, setRemoving] = useState<Sponsor | null>(null);

  const fail = useCallback(
    (error: unknown) =>
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      }),
    [t, toast]
  );

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    sponsorsService
      .list(slug)
      .then((rows) => {
        if (cancelled) return;
        setSponsors(rows.data);
        setInherited(rows.inherited);
      })
      .catch((error) => !cancelled && fail(error))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, fail]);

  const handleReorder = async (next: Sponsor[]) => {
    if (!slug) return;
    const before = sponsors;
    setSponsors(next);
    try {
      setSponsors((await sponsorsService.reorder(slug, next.map((s) => s.id))).data);
    } catch (error) {
      setSponsors(before);
      fail(error);
    }
  };

  const handleSaved = (saved: Sponsor) => {
    setSponsors((current) =>
      current.some((s) => s.id === saved.id) ? current.map((s) => (s.id === saved.id ? saved : s)) : [...current, saved]
    );
    setEditing(undefined);
    toast({ title: t('gymAdmin.saved') });
  };

  const handleRemove = async () => {
    if (!slug || !removing) return;
    const gone = removing;
    setRemoving(null);
    try {
      await sponsorsService.remove(slug, gone.id);
      setSponsors((current) => current.filter((s) => s.id !== gone.id));
    } catch (error) {
      fail(error);
    }
  };

  if (loading) return <Skeleton className="h-64 w-full max-w-4xl" />;

  return (
    <div className="max-w-4xl space-y-6">
      {place.stage && (
        <Card data-testid="inherited-sponsors">
          <CardHeader>
            <CardTitle>{t('gymAdmin.sponsors.inheritedTitle')}</CardTitle>
            <CardDescription>{t('gymAdmin.sponsors.inheritedHelp')}</CardDescription>
          </CardHeader>
          <CardContent>
            {inherited.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('gymAdmin.sponsors.inheritedEmpty')}</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {inherited.map((sponsor) => (
                  <li key={sponsor.id} className="flex items-center gap-3 p-2">
                    <SponsorSummary sponsor={sponsor} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>{t(place.stage ? 'gymAdmin.sponsors.stageTitle' : 'gymAdmin.sponsors.title')}</CardTitle>
            <CardDescription>{t('gymAdmin.sponsors.help')}</CardDescription>
          </div>
          <Button size="sm" className="shrink-0 gap-2" onClick={() => setEditing(null)}>
            <Plus className="h-4 w-4" />
            {t('gymAdmin.sponsors.add')}
          </Button>
        </CardHeader>
        <CardContent>
          {sponsors.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('gymAdmin.sponsors.empty')}</p>
          ) : (
            <SortableRows
              items={sponsors}
              onReorder={handleReorder}
              handleLabel={(sponsor) => t('gymAdmin.sponsors.reorder', { name: sponsor.name })}
              renderRow={(sponsor) => (
                <>
                  <SponsorSummary sponsor={sponsor} />
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(sponsor)}
                      aria-label={t('gymAdmin.sponsors.edit', { name: sponsor.name })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => setRemoving(sponsor)}
                      aria-label={t('gymAdmin.sponsors.remove', { name: sponsor.name })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            />
          )}
        </CardContent>
      </Card>

      {slug && editing !== undefined && (
        <SponsorFormDialog
          slug={slug}
          sponsor={editing}
          onSaved={handleSaved}
          onClose={() => setEditing(undefined)}
        />
      )}

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gymAdmin.sponsors.removeTitle', { name: removing?.name ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('gymAdmin.sponsors.removeDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** A sponsor's logo, name and link, as a row of either list shows it. */
function SponsorSummary({ sponsor }: { sponsor: Sponsor }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <div className="flex h-10 w-20 shrink-0 items-center justify-center rounded border bg-white p-1">
        {sponsor.logo_small_url && (
          <img src={sponsor.logo_small_url} alt="" className="max-h-full max-w-full object-contain" />
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{sponsor.name}</p>
        {sponsor.website_url && (
          <a
            href={sponsor.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-center gap-1 truncate text-xs text-muted-foreground hover:text-foreground"
          >
            <span className="truncate">{sponsor.website_url}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        )}
      </div>
    </div>
  );
}
